import React from 'react';
import { FormProvider } from 'react-hook-form';
import Add from '@mui/icons-material/Add';
import Button from '@mui/material/Button';
import translate from '@/lib/intl/translate';
import type PostOpportunityConfidenceLevel from '@/types/api/PostOpportunityConfidenceLevel';
import showErrorMessage from '@/utils/toasts/showErrorMessage';
import showSuccessMessage from '@/utils/toasts/showSuccessMessage';
import useConfidenceLevelForm from '../hooks/useConfidenceLevelForm';
import useConfidenceLevelServices from '../hooks/useConfidenceLevelServices';
import ConfidenceLevelForm from './ConfidenceLevelForm';

function AddConfidenceLevel() {
  const [isAdding, setIsAdding] = React.useState(false);
  const { postConfidenceLevel } = useConfidenceLevelServices();

  const handleFormSubmit = async (values: PostOpportunityConfidenceLevel) => {
    try {
      await postConfidenceLevel(values);
      showSuccessMessage(translate('CONFIDENCE_LEVEL_CREATE_SUCCESS'));
      setIsAdding(false);
    } catch (e) {
      showErrorMessage(translate('CONFIDENCE_LEVEL_CREATE_FAILURE'));
    }
  };

  const { formMethods, defaultValues } = useConfidenceLevelForm({
    amount: 0,
    name: '',
  });
  const { reset, handleSubmit } = formMethods;

  const handleCancel = React.useCallback(() => {
    setIsAdding(false);
    reset(defaultValues);
  }, [reset, defaultValues]);

  if (!isAdding) {
    return (
      <div className="grid max-w-5xl grid-cols-[1fr_96px] gap-4">
        <div>
          <Button
            onClick={() => setIsAdding(true)}
            className="p-0"
            startIcon={<Add />}
            data-testid="confidence-level-add"
          >
            {translate('CONFIDENCE_LEVEL_ADD_LEVEL')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="grid max-w-5xl grid-cols-[1fr_96px] gap-4">
      <FormProvider {...formMethods}>
        <form
          onSubmit={handleSubmit(handleFormSubmit)}
          data-testid="confidence-level-add-form"
        >
          <ConfidenceLevelForm isEditing onCancel={handleCancel} />
        </form>
      </FormProvider>
    </div>
  );
}

export default AddConfidenceLevel;
